// ═════════════════════════════════════════════════════════════════════════════
//  WHAT THE CREW READS ON A PRODUCTION REPORT
// ═════════════════════════════════════════════════════════════════════════════
// There are two lists in this product and they are deliberately different:
//
//   PRODUCTION ITEMS (material_types) — the ORG's catalogue. "8" Block" is the
//     same item on all nine jobs that bill it, and 243 production entries point
//     at it. Renaming one renames it everywhere.
//
//   CONTRACT LINES (project_contract_lines) — the JOB's schedule of values.
//     Langley Park calls its 8" Block line "8" Block wall"; another job calls
//     the same item something else. That is the point of it being per job.
//
// The owner renamed his contract lines and expected the field form to follow.
// It could not: the form lists production items. So the form now BORROWS the
// contract line's name for display, per project — Langley's crew reads "8"
// Block wall", Reign 1's crew still reads whatever Reign 1's contract calls it.
//
// WHAT IS STORED DOES NOT CHANGE. A report item keeps `type` = the production
// item's real name, because submit resolves the material_type_id by that exact
// name (js/app.js, typeByName[item.type]). Renaming what is PRINTED must never
// touch the key that links a week of production to the money.

// Builds { production item name → contract line name } for one project.
//
// Only a line that OWNS its item gets to rename it: if two contract lines on
// the same job point at the same production item (a change order adding more
// of the same), there is no single right name, so the item keeps its own and
// nothing is ambiguous on the crew's screen.
//
// `tasks` are the project's rows from project_task_names() — the field app
// already holds them in window.projectTasks[projectName], carrying
// {description, material_type_id}. `catalogue` is the production item list
// (needs {id, name}).
export function contractLabels(tasks, catalogue) {
  const nameById = new Map();
  (catalogue || []).forEach(c => { if (c && c.id && c.name) nameById.set(c.id, c.name); });

  const claims = new Map();          // material_type_id -> [description, …]
  (tasks || []).forEach(t => {
    if (!t || !t.material_type_id) return;
    const desc = String(t.description ?? '').trim();
    if (!desc) return;
    const list = claims.get(t.material_type_id) || [];
    list.push(desc);
    claims.set(t.material_type_id, list);
  });

  const out = new Map();
  claims.forEach((descs, typeId) => {
    if (descs.length !== 1) return;            // two lines, one item — ambiguous
    const itemName = nameById.get(typeId);
    if (!itemName) return;                     // item not in this crew's catalogue
    if (descs[0] === itemName) return;         // same word: nothing to override
    out.set(itemName, descs[0]);
  });
  return out;
}

// The name to PRINT for a production item. Falls back to the item's own name
// whenever this job's contract has nothing better to say — which is every job
// that has not been given line names of its own.
export function labelFor(labels, itemName) {
  if (!itemName) return '';
  return (labels && labels.get(itemName)) || itemName;
}
